/**
 * Detection arcs (08 §2.7, §4): one arc on a ring around the ranger for every enemy who is
 * noticing him, on the bearing to that enemy. It fills from its middle outward as his detection
 * meter fills and turns the alert accent once he is fighting; a `?` or `!` rides outside it, so
 * colour is never the only signal. Pure: geometry and selection only.
 */

import type { Enemy } from '../../../sim/types';
import { type GroundBasis, type Point, toHud } from './projection';

/** Below this the meter is noise: the same floor as the top-left detection glyph. */
export const DETECTION_MIN = 0.15;
/** The most arcs drawn at once; the strongest win. */
export const MAX_ARCS = 6;
/** Ring radius across the screen, logical pixels; the vertical radius is squashed by the camera pitch. */
export const RING_PX = 26;
/** Half the angle an arc spans when full, radians (22 degrees). */
export const ARC_HALF = (22 * Math.PI) / 180;
/** How far outside the ring the glyph sits, pixels. */
const GLYPH_OUT = 7;

export interface ArcSpec {
  id: number;
  /** Screen bearing from the ranger to the enemy, radians: 0 = right, PI / 2 = down the screen. */
  angle: number;
  /** 0..1: how much of the arc is lit. */
  fill: number;
  /** He is in combat: alert accent and `!`. */
  alert: boolean;
  glyph: '?' | '!';
}

/** The enemies worth an arc, strongest first: fighting men, then by detection meter. */
export function detectionArcs(
  enemies: readonly Enemy[],
  rangerX: number,
  rangerZ: number,
  basis: GroundBasis,
): ArcSpec[] {
  const arcs: ArcSpec[] = [];
  const centre = toHud(basis, rangerX, rangerZ);
  for (const enemy of enemies) {
    // Out of the fight: dead, incapacitated, or knocked down.
    if (enemy.state === 'dead' || enemy.state === 'wounded' || enemy.state === 'down') continue;
    const alert = enemy.state === 'combat';
    if (!alert && enemy.detection <= DETECTION_MIN) continue;
    const at = toHud(basis, enemy.x, enemy.z);
    arcs.push({
      id: enemy.id,
      angle: Math.atan2(at.y - centre.y, at.x - centre.x),
      fill: alert ? 1 : Math.min(1, Math.max(0, enemy.detection)),
      alert,
      glyph: alert ? '!' : '?',
    });
  }
  arcs.sort((a, b) => Number(b.alert) - Number(a.alert) || b.fill - a.fill || a.id - b.id);
  return arcs.slice(0, MAX_ARCS);
}

const round1 = (v: number): number => Math.round(v * 10) / 10;

/** A point of the ring's ellipse. */
export function ringPoint(centre: Point, rx: number, ry: number, angle: number): Point {
  return { x: round1(centre.x + Math.cos(angle) * rx), y: round1(centre.y + Math.sin(angle) * ry) };
}

/**
 * SVG path of the ring's arc from `from` to `to` radians (`to` > `from`, less than a full turn),
 * drawn the way screen angles grow: clockwise as the player sees it.
 */
export function arcPath(centre: Point, rx: number, ry: number, from: number, to: number): string {
  const a = ringPoint(centre, rx, ry, from);
  const b = ringPoint(centre, rx, ry, to);
  const large = to - from > Math.PI ? 1 : 0;
  return `M${a.x} ${a.y}A${round1(rx)} ${round1(ry)} 0 ${large} 1 ${b.x} ${b.y}`;
}

export interface ArcShape {
  /** The dim full-width track, and the lit part growing from its middle. */
  track: string;
  lit: string;
  glyph: Point;
}

/** Everything the view needs to draw one arc around `centre`; `squash` = vertical / horizontal radius. */
export function arcShape(spec: ArcSpec, centre: Point, squash: number): ArcShape {
  const rx = RING_PX;
  const ry = RING_PX * squash;
  // A sliver always shows, so a meter that has only just started still marks its bearing.
  const half = ARC_HALF * Math.max(0.08, spec.fill);
  return {
    track: arcPath(centre, rx, ry, spec.angle - ARC_HALF, spec.angle + ARC_HALF),
    lit: arcPath(centre, rx, ry, spec.angle - half, spec.angle + half),
    glyph: ringPoint(centre, rx + GLYPH_OUT, ry + GLYPH_OUT, spec.angle),
  };
}
